import React, { useState } from "react";

import styled from "styled-components/macro";

import { useHasFNC } from "Flocknote/stateHooks";

import AdminPageWrapper from "CrossFeatureComponents/AdminPageWrapper";
import { useCampaigns } from "Giving/API/queryHooks";
import { navSectionKeys } from "NavBar/navBarStore";
import { DateTime } from "luxon";

import FnrOptionSwitch from "UI/FnrOptionSwitch";
import FnrPageHeader, { headerVariants } from "UI/FnrPageHeader";
import FnrSlides from "UI/FnrSlides";
import { colors } from "UI/colors";

import capitalize from "Utilities/capitalize";

import CampaignList from "./CampaignList";
import UpgradeSplash from "./components/UpgradeSplash";
import { KEYS } from "./const";

const config = [
  { key: KEYS.LOADING, value: () => null },
  {
    key: KEYS.CURRENT,
    value: ({ currentCampaigns }) => (
      <CampaignList campaigns={currentCampaigns} era={KEYS.CURRENT} />
    ),
  },
  {
    key: KEYS.FUTURE,
    value: ({ futureCampaigns }) => (
      <CampaignList campaigns={futureCampaigns} era={KEYS.FUTURE} />
    ),
  },
  {
    key: KEYS.PAST,
    value: ({ pastCampaigns }) => (
      <CampaignList campaigns={pastCampaigns} era={KEYS.PAST} />
    ),
  },
];

const Campaigns = () => {
  const [currentKey, setCurrentKey] = useState(KEYS.CURRENT);
  const hasFNC = useHasFNC();

  const { campaigns, isLoading } = useCampaigns();

  const today = DateTime.now().startOf("day");

  const hasStarted = (campaign) =>
    DateTime.fromISO(campaign.startDate) <= today;

  // Ending a campaign by hand stamps today as its end date (see Network/
  // SettingsPanels/GivingCampaignSettingsPanel/EndCampaignButton.js), so
  // reaching the end date means ended — not ending. Open-ended campaigns have
  // no end date and never end.
  const hasEnded = (campaign) =>
    !!campaign.endDate && DateTime.fromISO(campaign.endDate) <= today;

  const currentCampaigns = campaigns?.filter(
    (campaign) => hasStarted(campaign) && !hasEnded(campaign)
  );

  const pastCampaigns = campaigns?.filter(hasEnded);

  const futureCampaigns = campaigns?.filter(
    (campaign) => !hasStarted(campaign)
  );

  if (hasFNC === false) return <UpgradeSplash />;

  return (
    <AdminPageWrapper
      isLoading={isLoading}
      navSectionKey={navSectionKeys.giving}
    >
      <CampaignsPage data-cy="Campaign_Page">
        <HeadingsWrapper>
          <Headings>
            <TitleFilterRow>
              <FnrPageHeader
                title="Campaigns"
                icon="fa-hand-holding-usd fad"
                variant={headerVariants.giving}
                subtext="Select a campaign to view its details and manage donations."
              />

              <FnrOptionSwitch
                options={[
                  { id: KEYS.CURRENT, value: capitalize(KEYS.CURRENT) },
                  { id: KEYS.FUTURE, value: capitalize(KEYS.FUTURE) },
                  { id: KEYS.PAST, value: capitalize(KEYS.PAST) },
                ]}
                selected={currentKey}
                onSelect={(option) => setCurrentKey(option.id)}
              />
            </TitleFilterRow>
          </Headings>
        </HeadingsWrapper>

        {/* <SwitchWrapper>
          <FnrOptionSwitch
            options={[
              { id: KEYS.CURRENT, value: capitalize(KEYS.CURRENT) },
              { id: KEYS.FUTURE, value: capitalize(KEYS.FUTURE) },
              { id: KEYS.PAST, value: capitalize(KEYS.PAST) },
            ]}
            selected={currentKey}
            onSelect={(option) => setCurrentKey(option.id)}
          />
        </SwitchWrapper> */}

        <ContentWrapper>
          <FnrSlides
            clip={false}
            activeKey={isLoading ? KEYS.LOADING : currentKey}
            fullWidth
            components={config}
            currentCampaigns={currentCampaigns}
            pastCampaigns={pastCampaigns}
            futureCampaigns={futureCampaigns}
          />
        </ContentWrapper>
      </CampaignsPage>
    </AdminPageWrapper>
  );
};

const CampaignsPage = styled.div`
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  container-name: campaigns_page;
  container-type: inline-size;
`;

const TitleFilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap-reverse;
`;

const HeadingsWrapper = styled.div`
  padding: 18px 0;
  background-color: ${colors.fnHeaderGray};
  border-bottom: 1px solid ${colors.fnLtGray};
`;

const Headings = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  display: grid;
  padding-inline: 1.5rem;
  /* z-index: 0; */
`;

const SwitchWrapper = styled.div`
  max-width: 800px;
  margin-inline: auto;
  margin-block: 1rem;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin-inline: auto;
  margin-block-start: 2rem;

  @container campaigns_page (width < 848px) {
    padding-inline: 1.5rem;
  }
`;

export default Campaigns;
